import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  HStack,
  Box,
  Icon,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
  Tooltip, // Adicionado Tooltip que faltava na primeira lista
} from "@chakra-ui/react";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import {
  FaSearch,
  FaEye,
  FaRegCalendarAlt,
  FaCog, // Consolidado aqui
} from "react-icons/fa";
import { AiFillPrinter } from "react-icons/ai";
import { useReactToPrint } from "react-to-print";
import { format } from "date-fns";

import Menu from "./Menu";
import { BoxTitle } from "../BoxTitle";
import SimpleDatePicker from '@/components/DatePicker/index';
import ConfiguracaoPopover from "../ConfiguracaoPopover";

// Interface definida apenas uma vez
interface QuickFilter {
  title: string;
  onClick: () => void;
  bgColor?: string;
}

interface Field {
  key: string;
  label: string;
}

interface HeaderProps {
  isMenuEnabled?: boolean;
  isEyeEnabled?: boolean;
  PrintEnabled?: boolean;
  isFilterVisible: boolean;
  onToggleFilter: () => void;
  onView?: (data: any) => void;
  printRef: React.RefObject<HTMLDivElement>;
  headers?: any;
  onHeadersChange?: (visibleHeaders: any) => void;
  title: string;
  searchTerm?: string;
  searchBar?: boolean;
  setSearchTerm?: (searchTerm: any) => void;
  filterComponent?: React.ReactNode;
  Calendar?: "period" | "day" | null;
  onDateInicioChange?: (data: string) => void;
  onDateFimChange?: (data: string) => void;
  defaultDataInicio?: string;
  defaultDataFim?: string;
  onPrint?: VoidFunction;
  customActions?: React.ReactNode[];
  management?: boolean;
  hasInitialPeriod?: boolean;
  quickFilters?: QuickFilter[];
  isNewLayoutMenu?: boolean
  visibleFields?: string[];
  exibirFields?: Field[];
  onVisibleFieldsChange?: (fields: string[]) => void;
}

const Header: React.FC<HeaderProps> = ({
  isMenuEnabled = false,
  isEyeEnabled = false,
  PrintEnabled = false,
  isFilterVisible = false,
  Calendar = null,
  onToggleFilter,
  isNewLayoutMenu,
  onView,
  printRef,
  headers: visibleHeaders,
  onHeadersChange,
  title,
  searchBar = true,
  searchTerm,
  setSearchTerm,
  filterComponent,
  onDateInicioChange,
  onDateFimChange,
  defaultDataInicio,
  defaultDataFim,
  customActions = [],
  management = false,
  onPrint,
  hasInitialPeriod = true,
  quickFilters = [],
  visibleFields = [],
  onVisibleFieldsChange,
  exibirFields = [],
}) => {
  const handlePrint = () => {
    if (onPrint) {
      onPrint();
      return;
    }
    hdnPrint();
  };

  const hdnPrint = useReactToPrint({ content: () => printRef.current });

  const mainIconSize = management ? 6 : 6;
  const mainTextSize = management ? "lg" : "xl";

  const RenderMenu = useCallback(
    () => (
      <Box textAlign="center">
        {visibleHeaders && onHeadersChange ? (
          <Menu headers={visibleHeaders} onFilter={onHeadersChange} isNewLayout={isNewLayoutMenu}>
            <Tooltip
              label="Ocultar campos"
              color={"rgba(0, 0, 0, 1)"}
              backgroundColor={"rgba(255, 255, 255, 0.8)"}
              border={"1px solid rgba(0, 0, 0, 0.8)"}
              rounded={"2xl"}
              placement="bottom"
              hasArrow
            >
              <span>
                <Icon
                  as={FaEye}
                  boxSize={mainIconSize}
                  color={isEyeEnabled ? "gray.800" : "gray.300"}
                  cursor={isEyeEnabled ? "pointer" : "not-allowed"}
                  onClick={isEyeEnabled ? onView : undefined}
                />
              </span>
            </Tooltip>
          </Menu>
        ) : (
          <Tooltip
            label="Ocultar campos"
            color={"rgba(0, 0, 0, 1)"}
            backgroundColor={"rgba(255, 255, 255, 0.8)"}
            border={"1px solid rgba(0, 0, 0, 0.8)"}
            rounded={"2xl"}
            placement="bottom"
            hasArrow
          >
            <span>
              <Icon
                as={FaEye}
                boxSize={mainIconSize}
                color="gray.300"
                cursor="not-allowed"
              />
            </span>
          </Tooltip>
        )}
      </Box>
    ),
    [visibleHeaders, onHeadersChange, onView, isEyeEnabled, mainIconSize]
  );

  const today = useMemo(() => new Date(), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date(today);
    date.setDate(today.getDate() - 30);
    return date;
  }, [today]);

  const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

  const getInitialDates = () => {
    // Se as datas iniciais foram fornecidas via props, usar elas
    if (defaultDataInicio && defaultDataFim) {
      return {
        inicio: defaultDataInicio,
        fim: defaultDataFim,
      };
    }
    // Caso contrário, usar a lógica padrão
    if (hasInitialPeriod) {
      return {
        inicio: formatDate(thirtyDaysAgo),
        fim: formatDate(today),
      };
    }
    return {
      inicio: "",
      fim: "",
    };
  };

  const initialDates = getInitialDates();
  const [dataInicio, setDataInicio] = useState(initialDates.inicio);
  const [dataFim, setDataFim] = useState(initialDates.fim);

  // Sincronizar estados internos quando as props mudarem
  useEffect(() => {
    if (defaultDataInicio) {
      setDataInicio(defaultDataInicio);
    }
    if (defaultDataFim) {
      setDataFim(defaultDataFim);
    }
  }, [defaultDataInicio, defaultDataFim]);

  useEffect(() => {
    // Só disparar chamadas automáticas se hasInitialPeriod for true E não houver datas fornecidas via props
    if (Calendar === "period" && hasInitialPeriod && !defaultDataInicio && !defaultDataFim) {
      onDateInicioChange?.(formatDate(thirtyDaysAgo));
      onDateFimChange?.(formatDate(today));
    }
  }, [
    Calendar,
    hasInitialPeriod,
    onDateInicioChange,
    onDateFimChange,
    thirtyDaysAgo,
    today,
    defaultDataInicio,
    defaultDataFim,
  ]);

  const renderDatePicker = () => (
    <Popover>
      <PopoverTrigger>
        <Box>
          <Tooltip
            label="Selecione data"
            color={"rgba(0, 0, 0, 1)"}
            backgroundColor={"rgba(255, 255, 255, 0.8)"}
            border={"1px solid rgba(0, 0, 0, 0.8)"}
            rounded={"2xl"}
            placement="bottom"
            hasArrow
          >
            <span>
              <Icon
                as={FaRegCalendarAlt}
                boxSize={mainIconSize}
                cursor={Calendar ? "pointer" : "not-allowed"}
                color={Calendar ? "gray.800" : "gray.300"}
              />
            </span>
          </Tooltip>
        </Box>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          {Calendar === "period" && (
            <HStack spacing={2}>
              <BoxTitle
                title="Data Início"
                children={
                  <SimpleDatePicker
                    onDateChange={(date: string) => {
                      setDataInicio(date);
                      onDateInicioChange?.(date);
                    }}
                    value={dataInicio}
                  />
                }
              />
              <BoxTitle
                title="Data Fim"
                children={
                  <SimpleDatePicker
                    onDateChange={(date: string) => {
                      setDataFim(date);
                      onDateFimChange?.(date);
                    }}
                    value={dataFim}
                  />
                }
              />
            </HStack>
          )}
          {Calendar === "day" && (
            <SimpleDatePicker
              onDateChange={(date: string) => {
                setDataInicio(date);
                onDateInicioChange?.(date);
              }}
              value={dataInicio}
            />
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );

  const renderQuickFilters = () => {
    if (!quickFilters || quickFilters.length === 0) return null;

    const defaultColors = ["teal.100", "orange.100", "green.100"];

    return (
      <HStack spacing={2}>
        {quickFilters.map((filter, index) => (
          <Button
            key={index}
            size="sm"
            borderRadius="full"
            bg={filter.bgColor || defaultColors[index % defaultColors.length]}
            color="gray.800"
            fontWeight="bold"
            fontSize="2xs"
            px={3}
            py={2}
            _hover={{
              opacity: 0.8,
              transform: "translateY(-1px)",
            }}
            _active={{
              transform: "translateY(0px)",
            }}
            onClick={filter.onClick}
          >
            {filter.title}
          </Button>
        ))}
      </HStack>
    );
  };

  // Bloco JSX (return) limpo e corrigido
  return (
    <Box
      border={management ? "0px" : "2px"}
      borderBottom={management ? "2px" : "2px"}
      borderColor="#0f4f6d"
      borderRadius={management ? "none" : "xl"}
      p={2}
    >
      <HStack justify="space-between" align="center">
        {/* Título à esquerda */}
        <HStack>
          <Icon boxSize={mainIconSize} as={IoMdCheckmarkCircleOutline} />
          <Text fontWeight="bold" fontSize={mainTextSize}>
            {title}
          </Text>
        </HStack>
      
        {/* Filtros rápidos centralizados */}
        {renderQuickFilters()}

        <HStack spacing={4} align="center">
          {searchBar &&
            <Box textAlign="center">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none" color="gray.300">
                  <Icon as={FaSearch} boxSize={4} />
                </InputLeftElement>
                <Input
                  size="sm"
                  rounded="2xl"
                  placeholder="Pesquise"
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm && setSearchTerm(e.target.value)
                  }
                />
                <InputRightElement>{filterComponent}</InputRightElement>
              </InputGroup>
            </Box>
          }

          <RenderMenu />
          {Calendar && renderDatePicker()}

          {/* Versão correta da Impressora (com Tooltip) */}
          <Box textAlign="center">
            <Tooltip
              label="Imprimir"
              color={"rgba(0, 0, 0, 1)"}
              backgroundColor={"rgba(255, 255, 255, 0.8)"}
              border={"1px solid rgba(0, 0, 0, 0.8)"}
              rounded={"2xl"}
              placement="bottom"
              hasArrow
            >
              <span>
                <Icon
                  as={AiFillPrinter}
                  boxSize={mainIconSize}
                  cursor={PrintEnabled ? "pointer" : "not-allowed"}
                  onClick={PrintEnabled ? handlePrint : undefined}
                  color={PrintEnabled ? "gray.800" : "gray.300"}
                />
              </span>
            </Tooltip>
          </Box>

          {/* Ícone de Configurações */}
          {management && onVisibleFieldsChange && (
            <Box textAlign="center">
              <ConfiguracaoPopover
                exibirFields={exibirFields ?? []}
                visibleFields={visibleFields}
                onFieldsChange={onVisibleFieldsChange}
              >
                <Tooltip
                  label="Configurações"
                  placement="left"
                  color={"rgba(0, 0, 0, 1)"}
                  backgroundColor={"rgba(255, 255, 255, 0.8)"}
                  border={"1px solid rgba(0, 0, 0, 0.8)"}
                  rounded={"2xl"}
                  hasArrow
                >
                  <span>
                    <Icon
                      as={FaCog}
                      boxSize={5}
                      color="gray.800"
                      cursor="pointer"
                    />
                  </span>
                </Tooltip>
              </ConfiguracaoPopover>
            </Box>
          )}

          {/* Ações Customizadas (sem duplicação) */}
          {customActions.map((action, idx) => (
            <Box textAlign="center" key={idx}>
              {action}
            </Box>
          ))}
        </HStack>
      </HStack>
    </Box>
  );
};

export default Header;